import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { getSecondsFromDateString } from '../../utils/date'
import { EMPTY_RAW_SESSION_EVENT_DATA } from './constants'
import { findSessionFromEventId } from './functions'
import { eventsAdapter } from './slice'
import { RawDrillEventData, RawTimingEventData } from './types'

const entityAdapterSelectors = eventsAdapter.getSelectors<RootState>(
  (state) => state.events
)

export const {
  selectAll: selectAllEvents,
  selectById: selectEventById,
  selectIds: selectEventIds,
  selectEntities: selectEventEntities,
  selectTotal: selectTotalEvents
} = entityAdapterSelectors

export const selectSessionEventById = (sessionId: string) =>
  createSelector(selectEventEntities, (entities) => {
    const event = entities[sessionId]
    if (event && event.dataType === 'Session') {
      return event
    }
    return EMPTY_RAW_SESSION_EVENT_DATA
  })

export const getPendingDrill = (state: RootState) => {
  return state.events.pendingDrill
}

export const selectDrillEventById = (drillId: string | null | undefined) =>
  createSelector(selectEventEntities, (entities) => {
    const event = drillId ? entities[drillId] : undefined
    if (event && event.dataType === 'Drill') {
      return event
    }
    return null
  })

export const selectDrillNamesAndIdsForSession = createSelector(
  [selectAllEvents, (_, sessionId: string) => sessionId],
  (events, sessionId) =>
    events
      .filter(
        (event): event is RawDrillEventData =>
          event.dataType === 'Drill' && event.parentId === sessionId
      )
      .map((drill) => ({
        name: drill.data.name,
        id: drill.data.id
      }))
)

// Period Events //

export const selectPeriodEventsForSession = (sessionId: string) =>
  createSelector(selectEventEntities, (eventEntities) => {
    const sessionEvent = eventEntities[sessionId]

    if (!sessionEvent || sessionEvent.dataType !== 'Session') {
      return []
    }

    return Object.values(eventEntities).filter((event) => {
      const sessionEvent = findSessionFromEventId(event.id, eventEntities)
      return (
        event.hierarchy_level === 'Period' && sessionEvent?.id === sessionId
      )
    })
  })

export const selectCurrentPeriodEventForSession = (sessionId: string) =>
  createSelector(selectPeriodEventsForSession(sessionId), (events) => {
    // Sort period events by start time and look for the latest period //
    const sortedEvents = events
      .filter(
        (event) =>
          event.hierarchy_level === 'Period' && event.dataType === 'Timing'
      )
      .sort(
        (a, b) =>
          getSecondsFromDateString(b.startTime) -
          getSecondsFromDateString(a.startTime)
      )

    return sortedEvents.length > 0
      ? (sortedEvents[0] as RawTimingEventData)
      : null
  })

// UPDATED: Include both 'Started' and 'Paused' drills as active
export const selectActiveDrillEventForSession = (sessionId: string) =>
  createSelector(selectPeriodEventsForSession(sessionId), (events) => {
    // Sort drill events by start time and look for the first active drill (Started or Paused) //
    const sortedEvents = events
      .filter(
        (event) =>
          event.hierarchy_level === 'Period' &&
          event.dataType === 'Drill' &&
          (event.data.state === 'Started' || event.data.state === 'Paused') // Include both Started and Paused
      )
      .sort(
        (a, b) =>
          getSecondsFromDateString(a.startTime) -
          getSecondsFromDateString(b.startTime)
      )
    return sortedEvents.length > 0 ? sortedEvents[0] : null
  })

// Possession Events //

export const selectPossessionEventsForSession = (sessionId: string) =>
  createSelector(selectEventEntities, (eventEntities) => {
    const sessionEvent = eventEntities[sessionId]

    if (!sessionEvent || sessionEvent.dataType !== 'Session') {
      return []
    }

    return Object.values(eventEntities).filter((event) => {
      const sessionEvent = findSessionFromEventId(event.id, eventEntities)
      return (
        event.hierarchy_level === 'Possession' && sessionEvent?.id === sessionId
      )
    })
  })

export const selectActiveStoppageEventForSession = (sessionId?: string) =>
  createSelector(
    selectPossessionEventsForSession(sessionId ?? ''),
    (events) => {
      // Sort possession events by start time and look for the first active stoppage (data.possessionType === 'Stoppage') (without an end time) //
      const sortedEvents = events
        .filter(
          (event) =>
            event.hierarchy_level === 'Possession' &&
            event.properties?.possessionType === 'Stoppage' &&
            !event.endTime
        )
        .sort(
          (a, b) =>
            getSecondsFromDateString(a.startTime) -
            getSecondsFromDateString(b.startTime)
        )

      return sortedEvents.length > 0 ? sortedEvents[0] : null
    }
  )
